import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  Button,
  CircularProgress,
} from "@mui/material";
// import { saveAs } from "file-saver";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import "jspdf-autotable";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import DescriptionIcon from "@mui/icons-material/Description";

const TableStatic = () => {
  const [data, setData] = useState([]); // Default to an empty array
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [centerCodeFilter, setCenterCodeFilter] = useState("");

  const [pendingFilter, setPendingFilter] = useState("");
  const [onlineStatusFilter, setOnlineStatusFilter] = useState(""); // 'Online' | 'Offline' | ''
  const [lastSyncTimeFilter, setLastSyncTimeFilter] = useState("");


  // Fetch Static report data from API
  useEffect(() => {
    const examDetails = JSON.parse(localStorage.getItem("examDetails"));
    const examName = examDetails?.examName;
    const shift = examDetails?.shift;
  
    if (!examName || !shift) return;
  
    setLoading(true);
    axios
      .post("https://croireatpl.in/api/statics", { examName, shift })
      .then((response) => {
        console.log("API Response:", response.data);
        if (Array.isArray(response.data)) {
          setData(response.data);
        } else {
          console.error("API response is not an array");
        }
      })
      .catch((error) => {
        console.error("Error fetching Static data:", error);
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);
  

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Excel Export Function
  const handleExportExcel = () => {
    const ws = XLSX.utils.json_to_sheet(data); // Convert the data into a worksheet
    const wb = XLSX.utils.book_new(); // Create a new workbook
    XLSX.utils.book_append_sheet(wb, ws, "Static Report"); // Add the worksheet to the workbook
    XLSX.writeFile(wb, "static_report.xlsx"); // Trigger the download of the Excel file
  };

  // Filter data based on center code
    const filteredData = Array.isArray(data)
    ? data.filter((row) => {
        const matchesCenter = row.centerCode
          .toLowerCase()
          .includes(centerCodeFilter.toLowerCase());

        const matchesPending =
          pendingFilter === "" || row.totalPending >= Number(pendingFilter);

        const matchesOnlineStatus =
          onlineStatusFilter === "" ||
          (onlineStatusFilter === "Online" && row.isOnline) ||
          (onlineStatusFilter === "Offline" && !row.isOnline);

        const matchesLastSync =
          lastSyncTimeFilter === "" ||
          (row.lastSyncTime &&
            row.lastSyncTime
              .toLowerCase()
              .includes(lastSyncTimeFilter.toLowerCase()));

        return (
          matchesCenter &&
          matchesPending &&
          matchesOnlineStatus &&
          matchesLastSync
        );
      })
    : [];


  // PDF Export Function (As previously defined)
  const handleExportPDF = () => {
    const doc = new jsPDF();
    doc.setFontSize(16);
    doc.text("Static Report", 14, 16);

    const columns = [
      "S.No",
      "Operator Photo",
      "Operator Number",
      "Center Code",
      "Total Pending",
      "Last Sync Time",
      "Doze Mode",
      "Sync Message",
      "Device ID",
      "Online Status",
    ];

    const rows = data.map((row, index) => {
      const img = new Image();
      img.src = row.operatorPhoto;
      return [
        index + 1,
        img,
        row.operatorNumber,
        row.centerCode,
        row.totalPending,
        row.lastSyncTime,
        row.isDozeModeOn ? "On" : "Off",
        row.syncMessage,
        row.deviceID,
        row.isOnline ? "Online" : "Offline",
      ];
    });

    doc.autoTable({
      head: [columns],
      body: rows,
      startY: 20,
      theme: "striped",
      margin: { top: 30, left: 14, right: 14 },
      columnStyles: { 1: { cellWidth: 30 } },
    });

    doc.save("static_report.pdf");
  };

  return (
    <Box sx={{ margin: 4,}} >
      <Typography variant="h4" gutterBottom sx={{ fontWeight: "bold" }}>
      Statistics Report
      </Typography>

      {/* Filter Section */}
      <Box sx={{ display: "flex", gap: 2, mb: 4, flexWrap: "wrap" }}>
        <input
          type="text"
          value={centerCodeFilter}
          onChange={(e) => setCenterCodeFilter(e.target.value)}
          placeholder="Filter by Center Code"
          style={{ padding: "8px", width: "200px" }}
        />
        <input
          type="number"
          value={pendingFilter}
          onChange={(e) => setPendingFilter(e.target.value)}
          placeholder="Min Pending"
          style={{ padding: "8px", width: "150px" }}
        />
        <select
          value={onlineStatusFilter}
          onChange={(e) => setOnlineStatusFilter(e.target.value)}
          style={{ padding: "8px", width: "160px" }}
        >
          <option value="">All Tabs</option>
          <option value="Online">Online</option>
          <option value="Offline">Offline</option>
        </select>
        <input
          type="text"
          value={lastSyncTimeFilter}
          onChange={(e) => setLastSyncTimeFilter(e.target.value)}
          placeholder="Last Sync Time"
          style={{ padding: "8px", width: "180px" }}
        />
      </Box>


      {/* Export Buttons */}
      <Box sx={{ display: "flex", justifyContent: "space-between", marginBottom: 2 }}>
        <Button
          variant="contained"
          color="success"
          startIcon={<DescriptionIcon />}
          onClick={handleExportExcel}
        >
          Export to Excel
        </Button>
        <Button
          variant="contained"
          color="error"
          startIcon={<PictureAsPdfIcon />}
          onClick={handleExportPDF}
        >
          Export to PDF
        </Button>
      </Box>

      {/* Loading State */}
      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", padding: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper} sx={{ mt: 4 }}>
          <Table sx={{ minWidth: 650 }} aria-label="static report table">
            <TableHead>
              <TableRow>
                <TableCell sx={{ backgroundColor: "#1976d2", color: "white" }}>
                  S.No
                </TableCell>
                <TableCell sx={{ backgroundColor: "#1976d2", color: "white" }}>
                  Operator Photo
                </TableCell>
                <TableCell sx={{ backgroundColor: "#1976d2", color: "white" }}>
                  Operator Number
                </TableCell>
                <TableCell sx={{ backgroundColor: "#1976d2", color: "white" }}>
                  Center Code
                </TableCell>
                <TableCell sx={{ backgroundColor: "#1976d2", color: "white" }}>
                  Total Pending
                </TableCell>
                <TableCell sx={{ backgroundColor: "#1976d2", color: "white" }}>
                  Last Sync Time
                </TableCell>
                <TableCell sx={{ backgroundColor: "#1976d2", color: "white" }}>
                  Doze Mode
                </TableCell>
                <TableCell sx={{ backgroundColor: "#1976d2", color: "white" }}>
                  Sync Message
                </TableCell>
                <TableCell sx={{ backgroundColor: "#1976d2", color: "white" }}>
                  Device ID
                </TableCell>
                <TableCell sx={{ backgroundColor: "#1976d2", color: "white" }}>
                  Tab Battery %
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredData
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((row, index) => (
                  <TableRow key={row.deviceID}>
                    <TableCell>{index + 1}</TableCell>
                    <TableCell>
                      <img
                        src={row.operatorPhoto}
                        alt="Operator"
                        style={{
                          width: 50,
                          height: 50,
                          borderRadius: "50%",
                        }}
                      />
                    </TableCell>
                    <TableCell>{row.operatorNumber}</TableCell>
                    <TableCell>{row.centerCode}</TableCell>
                    <TableCell>{row.totalPending}</TableCell>
                    <TableCell>{row.lastSyncTime}</TableCell>
                    <TableCell>{row.isDozeModeOn ? "On" : "Off"}</TableCell>
                    <TableCell>{row.syncMessage}</TableCell>
                    <TableCell>{row.deviceID}</TableCell>
                    <TableCell>{`${row.currentBattery}%` || "N/A"}</TableCell>
                    {/* {frMatchPercentage !== "N/A" ? `${frMatchPercentage}%` : 'N/A'} */}
                  </TableRow>
                ))}
            </TableBody>
          </Table>

          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={filteredData.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </TableContainer>
      )}
    </Box>
  );
};

export default TableStatic;
